export type CustodyActorIdentityKind = 'AUTH0' | 'API_KEY' | 'OPENID' | 'ANONYMOUS' | 'SYSTEM';

export interface CustodyActorSnapshotV1 {
  identityKind: CustodyActorIdentityKind;
  userId?: string;
  name: string | null;
  email: string | null;
  roles: string[];
  effectiveAgencyIds: string[];
  previewAgencyId?: string;
}

export interface CustodyRequestMetadata {
  actor: CustodyActorSnapshotV1;
  requestId: string;
  correlationId: string;
  sourceIp?: string;
  userAgent?: string;
}

export type CustodyEventActionV1 =
  | 'AGENCY_OPERATOR_GRANT_CREATED'
  | 'AGENCY_OPERATOR_GRANT_REPLACED'
  | 'AGENCY_OPERATOR_GRANT_RENEWED'
  | 'AGENCY_OPERATOR_GRANT_REVOKED'
  | 'AGENCY_OPERATOR_GRANT_EXPIRED'
  | 'AGENCY_OPERATOR_RENEWAL_REQUESTED'
  | 'AGENCY_FINGERPRINT_POLICY_UPDATED'
  | 'AGENCY_OPERATOR_CERTIFICATE_UPLOADED'
  | 'AGENCY_OPERATOR_CERTIFICATE_REPLACED'
  | 'AGENCY_OPERATOR_CERTIFICATE_DOWNLOAD_AUTHORIZED'
  | 'TEAM_MEMBER_ADDED'
  | 'TEAM_MEMBER_REMOVED'
  | 'TEAM_MEMBER_ROLE_CHANGED'
  | 'USER_ROLE_CHANGED'
  | 'TEAM_AGENCY_ASSIGNED'
  | 'JOB_OWNER_TRANSFERRED'
  | 'JOB_OWNER_TRANSFERRED_OUT'
  | 'JOB_OWNER_TRANSFERRED_IN'
  | 'JOB_ARCHIVED'
  | 'JOB_UPLOAD_COMPLETED'
  | 'TEAM_ARCHIVED'
  | 'MEDIA_ACCESS_AUTHORIZED'
  | 'JOB_DOCUMENT_ACCESS_AUTHORIZED'
  | 'JOB_DOWNLOAD_REQUESTED'
  | 'MEDIA_PLAYBACK_DELIVERED'
  | 'MEDIA_PLAYBACK_REPORTED'
  | 'JOB_DOCUMENT_DELIVERED'
  | 'JOB_DOWNLOAD_DELIVERED'
  | 'AGENCY_PREVIEW_USED'
  | 'AGENCY_PREVIEW_STARTED'
  | 'AGENCY_PREVIEW_ENDED'
  | 'MUTATION_DENIED'
  | 'JOB_ACCESS_DENIED'
  | 'TEAM_ACCESS_DENIED'
  | 'JOB_REPORT_EDIT_AUTHORIZED'
  | 'JOB_REPORT_EDITED'
  | 'JOB_REPORT_CERTIFIED'
  | 'JOB_REPORT_CERTIFICATION_CLEARED'
  | 'JOB_TAGS_CHANGED'
  | 'JOB_METADATA_CHANGED'
  | 'JOB_LIBRARY_ASSOCIATED'
  | 'JOB_LIBRARY_DISASSOCIATED'
  | 'JOB_REPORT_REVISION_REQUESTED'
  | 'JOB_ASSET_EXPORT_AUTHORIZED'
  | 'JOB_EXPORT_REQUESTED'
  | 'MEDIA_SHARE_LINK_CREATION_AUTHORIZED'
  | 'MEDIA_SHARE_LINK_CREATED'
  | 'MEDIA_SHARE_LINK_ACCESS_AUTHORIZED'
  | 'JOB_TRANSCRIPT_GENERATED'
  | 'JOB_REPORT_GENERATED'
  | 'JOB_EXPORTED'
  | 'JOB_DOWNLOADED'
  | 'SHARE_LINK_USED'
  | 'AUDIT_LOG_ACCESSED'
  | 'AUDIT_LOG_EXPORTED';

export type CustodyResourceTypeV1 =
  | 'AGENCY_OPERATOR_GRANT'
  | 'AGENCY_OPERATOR_RENEWAL_REQUEST'
  | 'AGENCY_FINGERPRINT_POLICY'
  | 'AGENCY_OPERATOR_CERTIFICATE'
  | 'TEAM_MEMBERSHIP'
  | 'USER_ACCOUNT'
  | 'JOB'
  | 'TEAM'
  | 'AGENCY'
  | 'CUSTODY_AUDIT'
  | 'CUSTODY_AUDIT_EXPORT';

export type CustodyDetailValueV1 = string | number | boolean | null;

export interface CustodyAgencySnapshotV1 {
  id: string;
  name: string;
}

export interface CustodyRelatedResourceSnapshotV1 {
  type: CustodyResourceTypeV1;
  id: string;
}

export interface CustodyEventEnvelopeV1 {
  version: 1;
  eventId: string;
  occurredAt: string;
  actor: CustodyActorSnapshotV1;
  action: CustodyEventActionV1;
  outcome: 'SUCCEEDED' | 'DENIED';
  /** Null for global actions or content without an agency association. */
  agency: CustodyAgencySnapshotV1 | null;
  resource: {
    type: CustodyResourceTypeV1;
    id: string;
    userId?: string;
    relatedResources: CustodyRelatedResourceSnapshotV1[];
  };
  details: Record<string, CustodyDetailValueV1>;
  requestId: string;
  correlationId: string;
  sourceIp?: string;
  userAgent?: string;
}

export const CUSTODY_ACTIONS_V1: readonly CustodyEventActionV1[] = [
  'AGENCY_OPERATOR_GRANT_CREATED',
  'AGENCY_OPERATOR_GRANT_REPLACED',
  'AGENCY_OPERATOR_GRANT_RENEWED',
  'AGENCY_OPERATOR_GRANT_REVOKED',
  'AGENCY_OPERATOR_GRANT_EXPIRED',
  'AGENCY_OPERATOR_RENEWAL_REQUESTED',
  'AGENCY_FINGERPRINT_POLICY_UPDATED',
  'AGENCY_OPERATOR_CERTIFICATE_UPLOADED',
  'AGENCY_OPERATOR_CERTIFICATE_REPLACED',
  'AGENCY_OPERATOR_CERTIFICATE_DOWNLOAD_AUTHORIZED',
  'TEAM_MEMBER_ADDED',
  'TEAM_MEMBER_REMOVED',
  'TEAM_MEMBER_ROLE_CHANGED',
  'USER_ROLE_CHANGED',
  'TEAM_AGENCY_ASSIGNED',
  'JOB_OWNER_TRANSFERRED',
  'JOB_OWNER_TRANSFERRED_OUT',
  'JOB_OWNER_TRANSFERRED_IN',
  'JOB_ARCHIVED',
  'JOB_UPLOAD_COMPLETED',
  'TEAM_ARCHIVED',
  'MEDIA_ACCESS_AUTHORIZED',
  'JOB_DOCUMENT_ACCESS_AUTHORIZED',
  'JOB_DOWNLOAD_REQUESTED',
  'MEDIA_PLAYBACK_DELIVERED',
  'MEDIA_PLAYBACK_REPORTED',
  'JOB_DOCUMENT_DELIVERED',
  'JOB_DOWNLOAD_DELIVERED',
  'AGENCY_PREVIEW_USED',
  'AGENCY_PREVIEW_STARTED',
  'AGENCY_PREVIEW_ENDED',
  'MUTATION_DENIED',
  'JOB_ACCESS_DENIED',
  'TEAM_ACCESS_DENIED',
  'JOB_REPORT_EDIT_AUTHORIZED',
  'JOB_REPORT_EDITED',
  'JOB_REPORT_CERTIFIED',
  'JOB_REPORT_CERTIFICATION_CLEARED',
  'JOB_TAGS_CHANGED',
  'JOB_METADATA_CHANGED',
  'JOB_LIBRARY_ASSOCIATED',
  'JOB_LIBRARY_DISASSOCIATED',
  'JOB_REPORT_REVISION_REQUESTED',
  'JOB_ASSET_EXPORT_AUTHORIZED',
  'JOB_EXPORT_REQUESTED',
  'MEDIA_SHARE_LINK_CREATION_AUTHORIZED',
  'MEDIA_SHARE_LINK_CREATED',
  'MEDIA_SHARE_LINK_ACCESS_AUTHORIZED',
  'JOB_TRANSCRIPT_GENERATED',
  'JOB_REPORT_GENERATED',
  'JOB_EXPORTED',
  'JOB_DOWNLOADED',
  'SHARE_LINK_USED',
  'AUDIT_LOG_ACCESSED',
  'AUDIT_LOG_EXPORTED',
];

const CUSTODY_RESOURCES_V1: Record<CustodyEventActionV1, CustodyResourceTypeV1> = {
  AGENCY_OPERATOR_GRANT_CREATED: 'AGENCY_OPERATOR_GRANT',
  AGENCY_OPERATOR_GRANT_REPLACED: 'AGENCY_OPERATOR_GRANT',
  AGENCY_OPERATOR_GRANT_RENEWED: 'AGENCY_OPERATOR_GRANT',
  AGENCY_OPERATOR_GRANT_REVOKED: 'AGENCY_OPERATOR_GRANT',
  AGENCY_OPERATOR_GRANT_EXPIRED: 'AGENCY_OPERATOR_GRANT',
  AGENCY_OPERATOR_RENEWAL_REQUESTED: 'AGENCY_OPERATOR_RENEWAL_REQUEST',
  AGENCY_FINGERPRINT_POLICY_UPDATED: 'AGENCY_FINGERPRINT_POLICY',
  AGENCY_OPERATOR_CERTIFICATE_UPLOADED: 'AGENCY_OPERATOR_CERTIFICATE',
  AGENCY_OPERATOR_CERTIFICATE_REPLACED: 'AGENCY_OPERATOR_CERTIFICATE',
  AGENCY_OPERATOR_CERTIFICATE_DOWNLOAD_AUTHORIZED: 'AGENCY_OPERATOR_CERTIFICATE',
  TEAM_MEMBER_ADDED: 'TEAM_MEMBERSHIP',
  TEAM_MEMBER_REMOVED: 'TEAM_MEMBERSHIP',
  TEAM_MEMBER_ROLE_CHANGED: 'TEAM_MEMBERSHIP',
  USER_ROLE_CHANGED: 'USER_ACCOUNT',
  TEAM_AGENCY_ASSIGNED: 'TEAM',
  JOB_OWNER_TRANSFERRED: 'JOB',
  JOB_OWNER_TRANSFERRED_OUT: 'JOB',
  JOB_OWNER_TRANSFERRED_IN: 'JOB',
  JOB_ARCHIVED: 'JOB',
  JOB_UPLOAD_COMPLETED: 'JOB',
  TEAM_ARCHIVED: 'TEAM',
  MEDIA_ACCESS_AUTHORIZED: 'JOB',
  JOB_DOCUMENT_ACCESS_AUTHORIZED: 'JOB',
  JOB_DOWNLOAD_REQUESTED: 'JOB',
  MEDIA_PLAYBACK_DELIVERED: 'JOB',
  MEDIA_PLAYBACK_REPORTED: 'JOB',
  JOB_DOCUMENT_DELIVERED: 'JOB',
  JOB_DOWNLOAD_DELIVERED: 'JOB',
  AGENCY_PREVIEW_USED: 'AGENCY',
  AGENCY_PREVIEW_STARTED: 'AGENCY',
  AGENCY_PREVIEW_ENDED: 'AGENCY',
  MUTATION_DENIED: 'AGENCY',
  JOB_ACCESS_DENIED: 'JOB',
  TEAM_ACCESS_DENIED: 'TEAM',
  JOB_REPORT_EDIT_AUTHORIZED: 'JOB',
  JOB_REPORT_EDITED: 'JOB',
  JOB_REPORT_CERTIFIED: 'JOB',
  JOB_REPORT_CERTIFICATION_CLEARED: 'JOB',
  JOB_TAGS_CHANGED: 'JOB',
  JOB_METADATA_CHANGED: 'JOB',
  JOB_LIBRARY_ASSOCIATED: 'JOB',
  JOB_LIBRARY_DISASSOCIATED: 'JOB',
  JOB_REPORT_REVISION_REQUESTED: 'JOB',
  JOB_ASSET_EXPORT_AUTHORIZED: 'JOB',
  JOB_EXPORT_REQUESTED: 'JOB',
  MEDIA_SHARE_LINK_CREATION_AUTHORIZED: 'JOB',
  MEDIA_SHARE_LINK_CREATED: 'JOB',
  MEDIA_SHARE_LINK_ACCESS_AUTHORIZED: 'JOB',
  JOB_TRANSCRIPT_GENERATED: 'JOB',
  JOB_REPORT_GENERATED: 'JOB',
  JOB_EXPORTED: 'JOB',
  JOB_DOWNLOADED: 'JOB',
  SHARE_LINK_USED: 'JOB',
  AUDIT_LOG_ACCESSED: 'CUSTODY_AUDIT',
  AUDIT_LOG_EXPORTED: 'CUSTODY_AUDIT_EXPORT',
};

const CUSTODY_DETAILS_V1: Record<CustodyEventActionV1, readonly string[]> = {
  AGENCY_OPERATOR_GRANT_CREATED: [
    'expiresAt', 'fingerprintRequirement', 'fingerprintStatus', 'reason',
  ],
  AGENCY_OPERATOR_GRANT_REPLACED: [
    'expiresAt', 'fingerprintRequirement', 'fingerprintStatus', 'previousGrantId', 'reason',
  ],
  AGENCY_OPERATOR_GRANT_RENEWED: [
    'expiresAt', 'fingerprintRequirement', 'fingerprintStatus', 'previousExpiresAt',
  ],
  AGENCY_OPERATOR_GRANT_REVOKED: ['expiresAt', 'reason'],
  AGENCY_OPERATOR_GRANT_EXPIRED: ['expiresAt'],
  AGENCY_OPERATOR_RENEWAL_REQUESTED: ['expiresAt'],
  AGENCY_FINGERPRINT_POLICY_UPDATED: ['previousRequirement', 'requirement'],
  AGENCY_OPERATOR_CERTIFICATE_UPLOADED: ['byteSize', 'contentSha256', 'version'],
  AGENCY_OPERATOR_CERTIFICATE_REPLACED: ['byteSize', 'contentSha256', 'previousVersion', 'version'],
  AGENCY_OPERATOR_CERTIFICATE_DOWNLOAD_AUTHORIZED: [
    'authorizationMode', 'byteSize', 'contentSha256', 'storageVersionId', 'version',
  ],
  TEAM_MEMBER_ADDED: ['role', 'source'],
  TEAM_MEMBER_REMOVED: ['role', 'source'],
  TEAM_MEMBER_ROLE_CHANGED: ['previousRole', 'role', 'source'],
  USER_ROLE_CHANGED: ['role', 'changeKind'],
  TEAM_AGENCY_ASSIGNED: [],
  JOB_OWNER_TRANSFERRED: ['previousOwnerType', 'ownerType'],
  JOB_OWNER_TRANSFERRED_OUT: ['previousOwnerType', 'ownerType'],
  JOB_OWNER_TRANSFERRED_IN: ['previousOwnerType', 'ownerType'],
  JOB_ARCHIVED: [],
  JOB_UPLOAD_COMPLETED: ['byteCount', 'declaredPartCount', 'eTag', 'objectBindingId', 'versionId'],
  TEAM_ARCHIVED: [],
  MEDIA_ACCESS_AUTHORIZED: ['assetKind', 'authorizationMode'],
  JOB_DOCUMENT_ACCESS_AUTHORIZED: ['documentKind', 'authorizationMode'],
  JOB_DOWNLOAD_REQUESTED: ['authorizationMode', 'hasRange', 'recipientOverride'],
  MEDIA_PLAYBACK_DELIVERED: [
    'deliveryProvider', 'providerRequestId', 'statusCode', 'bytesSent', 'artifactId',
  ],
  // Player-reported: how much of the video one viewing session covered (REQ-41 partial playback).
  MEDIA_PLAYBACK_REPORTED: [
    'watchedSeconds', 'furthestPositionSeconds', 'durationSeconds', 'completion', 'reportSource',
  ],
  JOB_DOCUMENT_DELIVERED: [
    'deliveryProvider', 'providerRequestId', 'statusCode', 'bytesSent', 'artifactId',
  ],
  JOB_DOWNLOAD_DELIVERED: [
    'deliveryProvider', 'providerRequestId', 'statusCode', 'bytesSent', 'artifactId',
  ],
  AGENCY_PREVIEW_USED: ['parentType', 'resolver', 'operation'],
  AGENCY_PREVIEW_STARTED: ['previewSessionId', 'expiresAt'],
  AGENCY_PREVIEW_ENDED: ['previewSessionId', 'previewEndReason'],
  MUTATION_DENIED: ['parentType', 'resolver', 'operation', 'reasonCode'],
  JOB_ACCESS_DENIED: ['parentType', 'resolver', 'operation', 'reasonCode'],
  TEAM_ACCESS_DENIED: ['parentType', 'resolver', 'operation', 'reasonCode'],
  JOB_REPORT_EDIT_AUTHORIZED: ['editKind', 'authorizationMode'],
  JOB_REPORT_EDITED: ['editKind', 'versionId'],
  JOB_REPORT_CERTIFIED: ['versionId'],
  JOB_REPORT_CERTIFICATION_CLEARED: ['versionId'],
  JOB_TAGS_CHANGED: ['changeKind', 'previousCount', 'currentCount'],
  JOB_METADATA_CHANGED: ['changeKind', 'previousCount', 'currentCount'],
  JOB_LIBRARY_ASSOCIATED: [],
  JOB_LIBRARY_DISASSOCIATED: [],
  JOB_REPORT_REVISION_REQUESTED: ['authorizationMode'],
  JOB_ASSET_EXPORT_AUTHORIZED: ['assetKind', 'format', 'authorizationMode'],
  JOB_EXPORT_REQUESTED: ['authorizationMode'],
  MEDIA_SHARE_LINK_CREATION_AUTHORIZED: ['assetKind', 'authorizationMode'],
  MEDIA_SHARE_LINK_CREATED: ['assetKind', 'authorizationMode'],
  MEDIA_SHARE_LINK_ACCESS_AUTHORIZED: ['assetKind', 'authorizationMode'],
  JOB_TRANSCRIPT_GENERATED: ['artifactId'],
  JOB_REPORT_GENERATED: ['reportKind', 'artifactId'],
  JOB_EXPORTED: ['exportKind', 'assetKind', 'format', 'artifactId'],
  JOB_DOWNLOADED: [
    'assetKind', 'authorizationMode', 'artifactId',
    'deliveryProvider', 'providerRequestId', 'statusCode', 'bytesSent',
  ],
  SHARE_LINK_USED: ['assetKind', 'authorizationMode'],
  AUDIT_LOG_ACCESSED: ['filterDigest', 'scopeKind', 'timelinePreset', 'reasonCode'],
  AUDIT_LOG_EXPORTED: [
    'format', 'from', 'to', 'actorUserId', 'resourceType', 'resourceId', 'eventCount',
  ],
};

const GRANT_SNAPSHOT_KEYS = [
  'recipientName', 'recipientEmail', 'certificateVersion', 'certificateDocumentId', 'certificateContentSha256',
] as const;

const RECIPIENT_SNAPSHOT_KEYS = ['recipientName', 'recipientEmail'] as const;

export const CUSTODY_OPTIONAL_DETAILS_V1: Partial<Record<CustodyEventActionV1, readonly string[]>> = {
  TEAM_MEMBER_ADDED: RECIPIENT_SNAPSHOT_KEYS,
  TEAM_MEMBER_REMOVED: RECIPIENT_SNAPSHOT_KEYS,
  TEAM_MEMBER_ROLE_CHANGED: RECIPIENT_SNAPSHOT_KEYS,
  AGENCY_OPERATOR_GRANT_CREATED: GRANT_SNAPSHOT_KEYS,
  AGENCY_OPERATOR_GRANT_REPLACED: GRANT_SNAPSHOT_KEYS,
  AGENCY_OPERATOR_GRANT_RENEWED: [...GRANT_SNAPSHOT_KEYS, 'reason'],
  AGENCY_OPERATOR_GRANT_REVOKED: GRANT_SNAPSHOT_KEYS,
  AGENCY_OPERATOR_GRANT_EXPIRED: [...GRANT_SNAPSHOT_KEYS, 'reason'],
  AGENCY_OPERATOR_RENEWAL_REQUESTED: [...GRANT_SNAPSHOT_KEYS, 'reason'],
  AGENCY_OPERATOR_CERTIFICATE_UPLOADED: ['versionNumber'],
  AGENCY_OPERATOR_CERTIFICATE_REPLACED: ['versionNumber'],
  AGENCY_OPERATOR_CERTIFICATE_DOWNLOAD_AUTHORIZED: ['versionNumber'],
  AUDIT_LOG_EXPORTED: [
    'actions', 'actorEmailDigest', 'agencyId', 'agencyName', 'order', 'rawResourceId', 'scopeKind',
    'timelinePreset',
  ],
};

/**
 * The V1 maxima, exported because the event builder in `apps/backend` has to emit inside them.
 * Two private copies that agree today are one one-sided edit away from a builder that produces
 * exactly what this parser rejects.
 */
export const CUSTODY_MAX_ID_LENGTH = 128;
export const CUSTODY_MAX_DETAIL_LENGTH = 1024;
export const CUSTODY_MAX_SOURCE_IP_LENGTH = 64;
export const CUSTODY_MAX_USER_AGENT_LENGTH = 512;
export const CUSTODY_MAX_ACTOR_VALUES = 100;
export const CUSTODY_MAX_ENVELOPE_BYTES = 240 * 1024;
export const CUSTODY_MAX_ACTOR_NAME_LENGTH = 256;
export const CUSTODY_MAX_ACTOR_EMAIL_LENGTH = 320;
export const CUSTODY_MAX_AGENCY_NAME_LENGTH = 256;
export const CUSTODY_MAX_RELATED_RESOURCES = 20;

function invalidCustodyEnvelope(): never {
  throw new Error('Invalid custody event envelope.');
}

function recordWithExactKeys(
  value: unknown,
  required: readonly string[],
  optional: readonly string[] = [],
): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) invalidCustodyEnvelope();
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  const allowed = new Set([...required, ...optional]);
  if (required.some(key => !Object.prototype.hasOwnProperty.call(record, key))) invalidCustodyEnvelope();
  if (keys.some(key => !allowed.has(key))) invalidCustodyEnvelope();
  return record;
}

const UNSAFE_CUSTODY_CHARACTERS =
  /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/gu;

function boundedString(value: unknown, maxLength = CUSTODY_MAX_ID_LENGTH): value is string {
  return typeof value === 'string'
    && value.length > 0
    && value.length <= maxLength
    && value.trim() === value
    && value.replace(UNSAFE_CUSTODY_CHARACTERS, '') === value;
}

function canonicalTimestamp(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const timestamp = new Date(value);
  return Number.isFinite(timestamp.getTime()) && timestamp.toISOString() === value;
}

function canonicalStringArray(value: unknown, maxValues = CUSTODY_MAX_ACTOR_VALUES): value is string[] {
  if (!Array.isArray(value) || value.length > maxValues) return false;
  if (!value.every(item => boundedString(item))) return false;
  return value.every((item, index) => index === 0 || value[index - 1] < item);
}

function assertCustodyRelatedResourcesV1(
  value: unknown,
  primary: { type: CustodyResourceTypeV1; id: string },
): void {
  if (!Array.isArray(value) || value.length > CUSTODY_MAX_RELATED_RESOURCES) invalidCustodyEnvelope();
  const resourceTypes = new Set<CustodyResourceTypeV1>(Object.values(CUSTODY_RESOURCES_V1));
  let previous = '';
  // An event belongs to at most one video, whose deletion starts the release of its records.
  let jobId = primary.type === 'JOB' ? primary.id : undefined;
  for (const item of value) {
    const resource = recordWithExactKeys(item, ['type', 'id']);
    if (!resourceTypes.has(resource.type as CustodyResourceTypeV1) || !boundedString(resource.id)) {
      invalidCustodyEnvelope();
    }
    const key = `${String(resource.type)}\0${String(resource.id)}`;
    if (key <= previous || resource.type === primary.type && resource.id === primary.id) {
      invalidCustodyEnvelope();
    }
    if (resource.type === 'JOB') {
      if (jobId !== undefined) invalidCustodyEnvelope();
      jobId = resource.id;
    }
    previous = key;
  }
}

function assertCustodyActorV1(value: unknown): void {
  const actor = recordWithExactKeys(
    value,
    ['identityKind', 'name', 'email', 'roles', 'effectiveAgencyIds'],
    ['userId', 'previewAgencyId'],
  );
  const identityKinds: readonly CustodyActorIdentityKind[] = [
    'AUTH0', 'API_KEY', 'OPENID', 'ANONYMOUS', 'SYSTEM',
  ];
  if (!identityKinds.includes(actor.identityKind as CustodyActorIdentityKind)) invalidCustodyEnvelope();
  const requiresUserId = actor.identityKind !== 'ANONYMOUS' && actor.identityKind !== 'SYSTEM';
  if (requiresUserId !== Object.prototype.hasOwnProperty.call(actor, 'userId')) invalidCustodyEnvelope();
  if (requiresUserId && !boundedString(actor.userId)) invalidCustodyEnvelope();
  if (actor.name !== null && !boundedString(actor.name, CUSTODY_MAX_ACTOR_NAME_LENGTH)) invalidCustodyEnvelope();
  if (actor.email !== null && (!boundedString(actor.email, CUSTODY_MAX_ACTOR_EMAIL_LENGTH)
    || actor.email !== actor.email.toLowerCase())) invalidCustodyEnvelope();
  if (!requiresUserId && (actor.name !== null || actor.email !== null)) invalidCustodyEnvelope();
  if (!canonicalStringArray(actor.roles) || !canonicalStringArray(actor.effectiveAgencyIds, Infinity)) {
    invalidCustodyEnvelope();
  }
  if (actor.previewAgencyId !== undefined && !boundedString(actor.previewAgencyId)) invalidCustodyEnvelope();
}

function assertCustodyDetailsV1(action: CustodyEventActionV1, value: unknown): void {
  const details = recordWithExactKeys(
    value,
    CUSTODY_DETAILS_V1[action],
    CUSTODY_OPTIONAL_DETAILS_V1[action] ?? [],
  );
  for (const [key, detail] of Object.entries(details)) {
    if (key === 'recipientName') {
      if (!boundedString(detail, CUSTODY_MAX_ACTOR_NAME_LENGTH)) invalidCustodyEnvelope();
    } else if (key === 'recipientEmail') {
      if (!boundedString(detail, CUSTODY_MAX_ACTOR_EMAIL_LENGTH)
        || detail !== detail.toLowerCase()) invalidCustodyEnvelope();
    } else if (key === 'certificateVersion' || key === 'certificateDocumentId') {
      if (!boundedString(detail)) invalidCustodyEnvelope();
    } else if (key === 'certificateContentSha256') {
      if (typeof detail !== 'string' || !/^[a-f0-9]{64}$/.test(detail)) invalidCustodyEnvelope();
    } else if (key === 'versionNumber') {
      if (!Number.isSafeInteger(detail) || (detail as number) < 1) invalidCustodyEnvelope();
    } else if (key === 'expiresAt' || key === 'previousExpiresAt' || key === 'from' || key === 'to') {
      if (!canonicalTimestamp(detail)) invalidCustodyEnvelope();
    } else if (key === 'previewSessionId') {
      if (!boundedString(detail)) invalidCustodyEnvelope();
    } else if (key === 'previewEndReason') {
      if (detail !== 'MANUAL' && detail !== 'EXPIRED') invalidCustodyEnvelope();
    } else if (action === 'AUDIT_LOG_EXPORTED' && key === 'format') {
      if (detail !== 'CSV' && detail !== 'PDF') invalidCustodyEnvelope();
    } else if (action === 'AUDIT_LOG_EXPORTED' && key === 'eventCount') {
      if (!Number.isSafeInteger(detail) || (detail as number) < 0) {
        invalidCustodyEnvelope();
      }
    } else if ((action === 'AGENCY_OPERATOR_CERTIFICATE_UPLOADED'
      || action === 'AGENCY_OPERATOR_CERTIFICATE_REPLACED'
      || action === 'AGENCY_OPERATOR_CERTIFICATE_DOWNLOAD_AUTHORIZED') && key === 'byteSize') {
      if (!Number.isSafeInteger(detail) || (detail as number) < 1) invalidCustodyEnvelope();
    } else if ((action === 'AGENCY_OPERATOR_CERTIFICATE_UPLOADED'
      || action === 'AGENCY_OPERATOR_CERTIFICATE_REPLACED'
      || action === 'AGENCY_OPERATOR_CERTIFICATE_DOWNLOAD_AUTHORIZED') && key === 'contentSha256') {
      if (typeof detail !== 'string' || !/^[a-f\d]{64}$/.test(detail)) invalidCustodyEnvelope();
    } else if ((action === 'AGENCY_OPERATOR_CERTIFICATE_UPLOADED'
      || action === 'AGENCY_OPERATOR_CERTIFICATE_REPLACED'
      || action === 'AGENCY_OPERATOR_CERTIFICATE_DOWNLOAD_AUTHORIZED')
      && (key === 'version' || key === 'previousVersion')) {
      if (!boundedString(detail)) invalidCustodyEnvelope();
    } else if (action === 'AGENCY_OPERATOR_CERTIFICATE_DOWNLOAD_AUTHORIZED'
      && key === 'storageVersionId') {
      if (!boundedString(detail, CUSTODY_MAX_DETAIL_LENGTH)) invalidCustodyEnvelope();
    } else if (action === 'AGENCY_OPERATOR_CERTIFICATE_DOWNLOAD_AUTHORIZED'
      && key === 'authorizationMode') {
      if (detail !== 'SUPER_ADMIN' && detail !== 'OPERATOR_GRANT') invalidCustodyEnvelope();
    } else if (action === 'JOB_UPLOAD_COMPLETED' && key === 'byteCount') {
      if (!Number.isSafeInteger(detail) || (detail as number) < 0) invalidCustodyEnvelope();
    } else if (action === 'JOB_UPLOAD_COMPLETED' && key === 'declaredPartCount') {
      if (!Number.isSafeInteger(detail) || (detail as number) < 1) invalidCustodyEnvelope();
    } else if (action === 'JOB_UPLOAD_COMPLETED' && key === 'objectBindingId') {
      if (typeof detail !== 'string' || !/^OBJECT#[0-9a-f]{64}$/.test(detail)) invalidCustodyEnvelope();
    } else if (action === 'JOB_UPLOAD_COMPLETED' && (key === 'versionId' || key === 'eTag')) {
      if (!boundedString(detail, CUSTODY_MAX_DETAIL_LENGTH)) invalidCustodyEnvelope();
    } else if ((action === 'AUDIT_LOG_EXPORTED' || action === 'AUDIT_LOG_ACCESSED')
      && key === 'scopeKind') {
      if (detail !== 'GLOBAL' && detail !== 'AGENCY') invalidCustodyEnvelope();
    } else if ((action === 'AUDIT_LOG_EXPORTED' || action === 'AUDIT_LOG_ACCESSED')
      && key === 'timelinePreset') {
      if (detail !== 'GLOBAL' && detail !== 'AGENCY' && detail !== 'FILE') invalidCustodyEnvelope();
    } else if (action === 'AUDIT_LOG_EXPORTED' && key === 'order') {
      if (detail !== 'ASC' && detail !== 'DESC') invalidCustodyEnvelope();
    } else if ((action === 'AUDIT_LOG_EXPORTED' && key === 'actorEmailDigest')
      || (action === 'AUDIT_LOG_ACCESSED' && key === 'filterDigest')) {
      if (detail !== null && (typeof detail !== 'string' || !/^sha256-[0-9a-f]{64}$/.test(detail))) {
        invalidCustodyEnvelope();
      }
    } else if (action === 'AUDIT_LOG_EXPORTED' && key === 'agencyName') {
      if (detail !== null && !boundedString(detail, CUSTODY_MAX_AGENCY_NAME_LENGTH)) invalidCustodyEnvelope();
    } else if (action === 'AUDIT_LOG_EXPORTED' && key === 'actions') {
      if (detail !== null && !boundedString(detail, CUSTODY_MAX_DETAIL_LENGTH)) invalidCustodyEnvelope();
    } else if (action === 'AUDIT_LOG_EXPORTED'
      && ['actorUserId', 'agencyId', 'rawResourceId', 'resourceType', 'resourceId'].includes(key)) {
      if (detail !== null && !boundedString(detail)) invalidCustodyEnvelope();
    } else if (key === 'previousGrantId') {
      if (!boundedString(detail)) invalidCustodyEnvelope();
    } else if (key === 'fingerprintRequirement' || key === 'requirement') {
      if (detail !== 'REQUIRED' && detail !== 'NOT_REQUIRED') invalidCustodyEnvelope();
    } else if (key === 'previousRequirement') {
      if (detail !== null && detail !== 'REQUIRED' && detail !== 'NOT_REQUIRED') invalidCustodyEnvelope();
    } else if (key === 'fingerprintStatus') {
      if (detail !== 'YES' && detail !== 'NO' && detail !== 'NOT_APPLICABLE') invalidCustodyEnvelope();
    } else if (action === 'USER_ROLE_CHANGED' && key === 'role') {
      if (!['Admin', 'Agency sponsor', 'Operator', 'Super Admin', 'Trainer', 'Uploader'].includes(detail as string)) {
        invalidCustodyEnvelope();
      }
    } else if (action === 'USER_ROLE_CHANGED' && key === 'changeKind') {
      if (detail !== 'ASSIGNED' && detail !== 'REVOKED') invalidCustodyEnvelope();
    } else if (key === 'role' || key === 'previousRole') {
      if (!['Owner', 'Admin', 'Trainer', 'Member', 'Viewer'].includes(detail as string)) {
        invalidCustodyEnvelope();
      }
    } else if (key === 'source') {
      if (!['MANUAL', 'INVITATION', 'RECONCILIATION'].includes(detail as string)) {
        invalidCustodyEnvelope();
      }
    } else if (key === 'ownerType' || key === 'previousOwnerType') {
      if (detail !== 'USER' && detail !== 'TEAM') invalidCustodyEnvelope();
    } else if (action === 'JOB_EXPORTED' && key === 'assetKind' && detail === null) {
      // A full-job export is a bundle, not one shareable asset.
    } else if (action === 'MEDIA_ACCESS_AUTHORIZED' && key === 'assetKind') {
      if (![
        'PLAYBACK', 'POSTER', 'SLIDE_DECK', 'SUBTOPIC_THUMBNAIL',
        'JOB_THUMBNAIL_UPLOAD', 'REPORT_IMAGE_UPLOAD',
      ].includes(detail as string)) invalidCustodyEnvelope();
    } else if (key === 'assetKind') {
      if (!['REPORT', 'NOTES', 'TRANSCRIPT', 'AUDIO', 'VIDEO'].includes(detail as string)) {
        invalidCustodyEnvelope();
      }
    } else if (key === 'editKind') {
      if (![
        'REPORT', 'NOTES', 'MANUAL_REPORT', 'CUSTOMER_FOLLOW_UP_LETTER',
        'DUAL_PLUS_REPORT', 'TRANSLATED_DOCUMENT', 'FEEDBACK', 'MULTIPLE',
      ].includes(detail as string)) invalidCustodyEnvelope();
    } else if (key === 'versionId') {
      const generatedVersion = typeof detail === 'string' && detail.match(
        // eslint-disable-next-line max-len
        /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z)-[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/,
      );
      const hashedVersion = typeof detail === 'string' && /^sha256-[0-9a-f]{64}$/.test(detail);
      if (!boundedString(detail)
        || !hashedVersion && (!generatedVersion || !canonicalTimestamp(generatedVersion[1]))) {
        invalidCustodyEnvelope();
      }
    } else if (key === 'artifactId') {
      if (typeof detail !== 'string' || !/^sha256-[0-9a-f]{64}$/.test(detail)) {
        invalidCustodyEnvelope();
      }
    } else if (key === 'reportKind') {
      if (![
        'PUBLIC_SAFETY_REPORT', 'PUBLIC_SAFETY_NOTES', 'CUSTOMER_FOLLOW_UP_LETTER',
      ].includes(detail as string)) invalidCustodyEnvelope();
    } else if (key === 'exportKind') {
      if (detail !== 'DOCUMENT' && detail !== 'FULL_JOB') invalidCustodyEnvelope();
    } else if (key === 'changeKind') {
      if (!['ADDED', 'CLEARED', 'REPLACED'].includes(detail as string)) invalidCustodyEnvelope();
    } else if (key === 'previousCount' || key === 'currentCount') {
      if (!Number.isSafeInteger(detail) || (detail as number) < 0 || (detail as number) > 10_000) {
        invalidCustodyEnvelope();
      }
    } else if (action === 'JOB_EXPORTED' && key === 'format' && detail === null) {
      // A full-job export preserves source formats rather than producing PDF or DOCX.
    } else if (action === 'JOB_ASSET_EXPORT_AUTHORIZED' && key === 'format') {
      // Transcript downloads are TXT/RTF and bookmark-list clip exports are EDL.
      if (!['PDF', 'DOCX', 'TXT', 'RTF', 'EDL'].includes(detail as string)) invalidCustodyEnvelope();
    } else if (key === 'format') {
      if (detail !== 'PDF' && detail !== 'DOCX') invalidCustodyEnvelope();
    } else if (key === 'documentKind') {
      if (![
        'ORIGINAL', 'TRANSLATED', 'REPORT', 'NOTES', 'MANUAL_REPORT', 'FOLLOW_UP_LETTER', 'DUAL_PLUS_REPORT',
      ].includes(detail as string)) invalidCustodyEnvelope();
    } else if (key === 'authorizationMode') {
      if (!['PUBLIC', 'TRUSTED', 'PREVIEW', 'TEAM_EDIT', 'JOB_EDIT', 'SHARE_GRANT'].includes(detail as string)) {
        invalidCustodyEnvelope();
      }
    } else if (key === 'hasRange' || key === 'recipientOverride') {
      if (typeof detail !== 'boolean') invalidCustodyEnvelope();
    } else if (key === 'deliveryProvider') {
      if (detail !== 'CLOUDFRONT' && detail !== 'S3') invalidCustodyEnvelope();
    } else if (key === 'providerRequestId') {
      if (!boundedString(detail)) invalidCustodyEnvelope();
    } else if (key === 'statusCode') {
      if (!Number.isSafeInteger(detail) || (detail as number) < 200 || (detail as number) > 299) {
        invalidCustodyEnvelope();
      }
    } else if (key === 'bytesSent') {
      if (!Number.isSafeInteger(detail) || (detail as number) < 1) invalidCustodyEnvelope();
    } else if (key === 'watchedSeconds' || key === 'furthestPositionSeconds' || key === 'durationSeconds') {
      if (!Number.isSafeInteger(detail) || (detail as number) < 0 || (detail as number) > 7 * 24 * 3600) {
        invalidCustodyEnvelope();
      }
    } else if (key === 'completion') {
      if (detail !== 'PARTIAL' && detail !== 'COMPLETE') invalidCustodyEnvelope();
    } else if (key === 'reportSource') {
      if (detail !== 'PLAYER') invalidCustodyEnvelope();
    } else if (key === 'parentType') {
      if (!['Query', 'Mutation', 'Subscription', 'Job', 'GetGPTAnswerMetadata', 'Nested'].includes(detail as string)) {
        invalidCustodyEnvelope();
      }
    } else if (key === 'resolver') {
      if (!boundedString(detail)) invalidCustodyEnvelope();
    } else if (key === 'operation') {
      if (!['READ', 'SIGN', 'DOWNLOAD', 'MUTATE'].includes(detail as string)) invalidCustodyEnvelope();
    } else if (key === 'reasonCode') {
      if (action === 'AUDIT_LOG_ACCESSED') {
        if (detail !== null && detail !== 'NOT_AUTHORIZED') invalidCustodyEnvelope();
      } else if (detail !== 'NOT_AUTHORIZED' && detail !== 'PREVIEW_READ_ONLY') {
        invalidCustodyEnvelope();
      }
    } else if (key === 'reason') {
      if (!boundedString(detail, CUSTODY_MAX_DETAIL_LENGTH)) invalidCustodyEnvelope();
    } else {
      invalidCustodyEnvelope();
    }
  }
  if (action === 'AUDIT_LOG_EXPORTED') {
    const { resourceType, resourceId } = details;
    const resourceTypes: readonly CustodyResourceTypeV1[] = Object.values(CUSTODY_RESOURCES_V1);
    if ((resourceType === null) !== (resourceId === null)
      || resourceType !== null && !resourceTypes.includes(resourceType as CustodyResourceTypeV1)
      || (details.from as string) > (details.to as string)) {
      invalidCustodyEnvelope();
    }
  }
  if (action === 'MEDIA_SHARE_LINK_ACCESS_AUTHORIZED'
    && (details.authorizationMode !== 'SHARE_GRANT'
      || !['AUDIO', 'VIDEO'].includes(details.assetKind as string))) {
    invalidCustodyEnvelope();
  }
  if (action === 'SHARE_LINK_USED'
    && (details.authorizationMode !== 'SHARE_GRANT'
      || !['AUDIO', 'VIDEO'].includes(details.assetKind as string))) {
    invalidCustodyEnvelope();
  }
  if (action === 'JOB_EXPORTED') {
    const document = details.exportKind === 'DOCUMENT';
    if (document !== (details.assetKind !== null && details.format !== null)
      || document && !['REPORT', 'NOTES', 'TRANSCRIPT'].includes(details.assetKind as string)) {
      invalidCustodyEnvelope();
    }
  }
  if ((action === 'MEDIA_SHARE_LINK_CREATION_AUTHORIZED' || action === 'MEDIA_SHARE_LINK_CREATED')
    && (!['TRUSTED', 'PUBLIC'].includes(details.authorizationMode as string)
      || !['AUDIO', 'VIDEO'].includes(details.assetKind as string))) {
    invalidCustodyEnvelope();
  }
  if (action === 'JOB_ASSET_EXPORT_AUTHORIZED'
    // PREVIEW is reachable only from a query (transcript download); preview mutations are refused.
    && (!['TRUSTED', 'PUBLIC', 'PREVIEW'].includes(details.authorizationMode as string)
      || ['TXT', 'RTF'].includes(details.format as string) && details.assetKind !== 'TRANSCRIPT'
      || details.format === 'EDL' && details.assetKind !== 'VIDEO')) {
    invalidCustodyEnvelope();
  }
  if ((action === 'JOB_REPORT_EDIT_AUTHORIZED'
    || action === 'JOB_REPORT_REVISION_REQUESTED'
    || action === 'JOB_EXPORT_REQUESTED')
    && details.authorizationMode !== 'JOB_EDIT') {
    invalidCustodyEnvelope();
  }
}

/** Strict trust-boundary assertion for persisted and transported V1 custody events. */
export function assertCustodyEventEnvelopeV1(value: unknown): asserts value is CustodyEventEnvelopeV1 {
  if (new TextEncoder().encode(JSON.stringify(value)).length > CUSTODY_MAX_ENVELOPE_BYTES) {
    throw new Error('Custody event exceeds the durable transport size limit.');
  }
  const envelope = recordWithExactKeys(
    value,
    [
      'version', 'eventId', 'occurredAt', 'actor', 'action', 'outcome', 'agency',
      'resource', 'details', 'requestId', 'correlationId',
    ],
    ['sourceIp', 'userAgent'],
  );
  if (envelope.version !== 1) invalidCustodyEnvelope();
  if (!boundedString(envelope.eventId) || !canonicalTimestamp(envelope.occurredAt)) invalidCustodyEnvelope();
  assertCustodyActorV1(envelope.actor);
  if (!CUSTODY_ACTIONS_V1.includes(envelope.action as CustodyEventActionV1)) invalidCustodyEnvelope();
  const action = envelope.action as CustodyEventActionV1;
  if (envelope.outcome !== 'SUCCEEDED' && envelope.outcome !== 'DENIED') invalidCustodyEnvelope();
  const deniedAction = action === 'MUTATION_DENIED' || action === 'JOB_ACCESS_DENIED'
    || action === 'TEAM_ACCESS_DENIED';
  if (deniedAction && envelope.outcome !== 'DENIED'
    || !deniedAction && action !== 'AUDIT_LOG_ACCESSED' && envelope.outcome === 'DENIED') {
    invalidCustodyEnvelope();
  }

  if (envelope.agency === null) {
    if (action !== 'AUDIT_LOG_EXPORTED' && action !== 'AUDIT_LOG_ACCESSED'
      && action !== 'USER_ROLE_CHANGED' && CUSTODY_RESOURCES_V1[action] !== 'JOB'
      && CUSTODY_RESOURCES_V1[action] !== 'TEAM') invalidCustodyEnvelope();
  } else {
    if (action === 'USER_ROLE_CHANGED') invalidCustodyEnvelope();
    const agency = recordWithExactKeys(envelope.agency, ['id', 'name']);
    if (!boundedString(agency.id) || !boundedString(agency.name, CUSTODY_MAX_AGENCY_NAME_LENGTH)) {
      invalidCustodyEnvelope();
    }
  }

  const resource = recordWithExactKeys(envelope.resource, ['type', 'id', 'relatedResources'], ['userId']);
  if (resource.type !== CUSTODY_RESOURCES_V1[action] || !boundedString(resource.id)) {
    invalidCustodyEnvelope();
  }
  const resourceNeedsUser = resource.type === 'AGENCY_OPERATOR_GRANT'
    || resource.type === 'AGENCY_OPERATOR_RENEWAL_REQUEST'
    || resource.type === 'AGENCY_OPERATOR_CERTIFICATE'
    || resource.type === 'TEAM_MEMBERSHIP'
    || resource.type === 'USER_ACCOUNT';
  if (resourceNeedsUser !== Object.prototype.hasOwnProperty.call(resource, 'userId')) invalidCustodyEnvelope();
  if (resourceNeedsUser && !boundedString(resource.userId)) invalidCustodyEnvelope();
  assertCustodyRelatedResourcesV1(resource.relatedResources, {
    type: resource.type as CustodyResourceTypeV1,
    id: resource.id,
  });
  if ((action === 'JOB_LIBRARY_ASSOCIATED' || action === 'JOB_LIBRARY_DISASSOCIATED')
    && (!(resource.relatedResources instanceof Array) || resource.relatedResources.length !== 1
      || (resource.relatedResources[0] as CustodyRelatedResourceSnapshotV1).type !== 'TEAM')) {
    invalidCustodyEnvelope();
  }

  assertCustodyDetailsV1(action, envelope.details);
  const details = envelope.details as Record<string, unknown>;
  if (envelope.agency !== null
    && (action === 'MEDIA_SHARE_LINK_CREATION_AUTHORIZED' || action === 'MEDIA_SHARE_LINK_CREATED')
    && details.authorizationMode !== 'TRUSTED') invalidCustodyEnvelope();
  if (action === 'AUDIT_LOG_ACCESSED'
    && ((envelope.outcome === 'DENIED') !== (details.reasonCode !== null))) invalidCustodyEnvelope();
  if (envelope.agency === null && (action === 'AUDIT_LOG_EXPORTED' || action === 'AUDIT_LOG_ACCESSED')
    && (details.scopeKind !== 'GLOBAL'
      || details.timelinePreset !== 'GLOBAL' && details.timelinePreset !== 'FILE')) invalidCustodyEnvelope();
  if (envelope.agency !== null && (action === 'AUDIT_LOG_EXPORTED' || action === 'AUDIT_LOG_ACCESSED')
    && (details.scopeKind === 'GLOBAL' || details.timelinePreset === 'GLOBAL')) invalidCustodyEnvelope();
  if (!boundedString(envelope.requestId) || !boundedString(envelope.correlationId)) invalidCustodyEnvelope();
  if (envelope.sourceIp !== undefined && !boundedString(envelope.sourceIp, CUSTODY_MAX_SOURCE_IP_LENGTH)) {
    invalidCustodyEnvelope();
  }
  if (envelope.userAgent !== undefined && !boundedString(envelope.userAgent, CUSTODY_MAX_USER_AGENT_LENGTH)) {
    invalidCustodyEnvelope();
  }
}
